import React, { useCallback, useContext, useRef } from 'react'

import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { useForm } from 'react-hook-form'

import { DeviceMobileIcon, LockLockedIcon, QrIcon } from '@arcade/web/icons/react'
import { ArcadeBorderedCard, LoadingImage, PaddingInset, spacing, Stack, Text } from '@cash-design-system/react'
import { BlockerAction } from '@cash-web/protos/squareup/franklin/blocker_action.pb'
import { FormBlocker as FormBlockerProps } from '@cash-web/protos/squareup/franklin/form_blocker.pb'
import logger from '@cash-web/shared/util-logger'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'
import { ErrorContext } from '@cash-web/shared-data-access-error'
import { NestedCard } from '@cash-web/shared-feature-common'
import { ModalErrorContent } from '@cash-web/shared-feature-modal'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'

import { BaseBlocker } from '../BaseBlocker/BaseBlocker'
import { ButtonElement } from '../FormBlocker/elements/ButtonElement/ButtonElement'
import { handleFormAction } from '../FormBlocker/handleFormAction'
import { TotpIconList } from './TotpIconList'

const QrCodeWrapper = styled(NestedCard)`
  padding: ${({ theme: { spacing } }) => `${spacing.xl} 4.5rem`};
  width: 100%;
`

const ArcadeQrCodeWrapper = styled(ArcadeBorderedCard)`
  padding: ${({ theme: { spacing } }) => `${spacing.xl} 4.5rem`};
  width: 100%;
`

const QrCodeImage = styled(LoadingImage)`
  height: 16rem;
  width: 16rem;
  margin: auto;
`

export function BlockerStartTotpEnrollment({ blocker }: { blocker: FormBlockerProps }): React.ReactElement {
  const form = useForm()
  const { darkMode } = useTheme()
  const { setError, clearError } = useContext(ErrorContext)
  const flow = useFlowContext()
  const selectedAction = useRef('')
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')
  const onBlockerAction = useCallback(
    async (action: BlockerAction) => {
      return (await flow.executeBlockerAction(action)) ?? null
    },
    [flow]
  )
  const onTotpStartAction = useCallback(
    async (action: BlockerAction) => {
      const { onFormResponse, onSubmit } = await handleFormAction(action, onBlockerAction, setError, clearError)

      if (action.submit_action) {
        await form.handleSubmit(data => onSubmit(data))()
      } else {
        onFormResponse(await onBlockerAction(action))
      }
    },
    [setError, clearError, form, onBlockerAction]
  )

  const textElement = (index: number) => blocker.elements?.[index]?.text_element?.text || ''
  const title = textElement(0)
  const stepOneTitle = textElement(1)
  const stepOneDescription = textElement(2)

  const stepTwoTitle = textElement(3)
  const stepTwoDescription = textElement(4)

  const stepThreeTitle = textElement(5)
  const stepThreeDescription = textElement(6)

  const qrCode = blocker.elements?.[7]?.remote_image_element?.image

  const buttons = []
  if (blocker.primary_action) {
    buttons.push(
      <ButtonElement
        action={blocker.primary_action}
        canSubmit={!!blocker.primary_action.submit_action}
        key="primary"
        selectedAction={selectedAction}
      />
    )
  }

  if (qrCode == null) {
    logger.error(new Error('QR code missing, customer cannot proceed with TOTP setup'))
    return <ModalErrorContent />
  }

  return (
    <BaseBlocker
      form={form}
      onSubmit={async () => {
        if (blocker.primary_action) {
          return await onTotpStartAction(blocker.primary_action)
        }
      }}
      buttons={buttons}
    >
      <Stack space={spacing.xxl}>
        <PaddingInset>
          <Text typography="numeralSmall" as="h2" color="textStandard">
            {title}
          </Text>
        </PaddingInset>

        <Stack justifyItems="center" space={spacing.defaultContent}>
          <TotpIconList
            items={[
              { Icon: DeviceMobileIcon, title: stepOneTitle, description: stepOneDescription },
              { Icon: QrIcon, title: stepTwoTitle, description: stepTwoDescription },
              { Icon: LockLockedIcon, title: stepThreeTitle, description: stepThreeDescription },
            ]}
          />

          {isArcadeMigrationEnabled ? (
            <ArcadeQrCodeWrapper data-testid="arcade-qr-code-wrapper">
              <QrCodeImage
                placeholderHeight="16rem"
                alt="Authenticator QR code"
                src={darkMode ? qrCode?.dark_url : qrCode?.light_url}
              />
            </ArcadeQrCodeWrapper>
          ) : (
            <QrCodeWrapper data-testid="nested-qr-code-wrapper">
              <QrCodeImage
                placeholderHeight="16rem"
                alt="Authenticator QR code"
                src={darkMode ? qrCode?.dark_url : qrCode?.light_url}
              />
            </QrCodeWrapper>
          )}
        </Stack>
      </Stack>
    </BaseBlocker>
  )
}
